import {
  Radio as MantineRadio,
  RadioCardProps as MantineRadioCardProps,
} from '@mantine/core';
import { forwardRef } from 'react';

import { RadioIndicator } from '../Radio/RadioIndicator';
import { ThemeIcon, ThemeIconColor } from '../ThemeIcon';
import styles from './RadioCardBox.module.css';

export interface RadioCardBoxProps extends Omit<
  MantineRadioCardProps,
  'style' | 'styles' | 'classNames'
> {
  /** Icon element displayed at the top center of the card */
  icon?: React.ReactNode;
  /** Color variant for the ThemeIcon wrapper. Default: 'blue' */
  iconColor?: ThemeIconColor;
  /** Primary title text (semi-bold, 14px) */
  title?: string;
  /** List of bullet-point strings displayed below the title */
  bulletPoints?: string[];
  /** When true, hides the RadioIndicator and disables pointer events (display-only mode) */
  isReadOnly?: boolean;
}

export const RadioCardBox = forwardRef<HTMLButtonElement, RadioCardBoxProps>(
  (
    {
      icon,
      iconColor = 'blue',
      title,
      bulletPoints,
      disabled,
      isReadOnly = false,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <MantineRadio.Card
        ref={ref}
        unstyled
        classNames={{ card: styles.card }}
        data-readonly={isReadOnly ? true : undefined}
        disabled={disabled}
        {...props}
      >
        {children ?? (
          <div className={styles.content}>
            <div className={styles.inputValue}>
              <div className={styles.titleGroup}>
                {icon && (
                  <ThemeIcon size="24" color={iconColor}>
                    {icon}
                  </ThemeIcon>
                )}
                {title && <span className={styles.title}>{title}</span>}
              </div>
              {bulletPoints && bulletPoints.length > 0 && (
                <div className={styles.bulletPoints}>
                  {bulletPoints.map((point) => (
                    <p key={point} className={styles.bulletPoint}>
                      {point}
                    </p>
                  ))}
                </div>
              )}
            </div>
            {!isReadOnly && (
              <div className={styles.radioRow}>
                <RadioIndicator disabled={disabled} />
              </div>
            )}
          </div>
        )}
      </MantineRadio.Card>
    );
  }
);

RadioCardBox.displayName = 'RadioCardBox';
